/*
  Generic dot-pagination carousel, one slide per view. Built for
  sections/custom-browse-collections.liquid's mobile carousel option,
  but not tied to it by name — any section can reuse it by wrapping a
  scrollable track and a row of dot buttons in <mobile-carousel>, using
  the data-carousel-track / data-carousel-dots hooks below (BEM classes
  for look-and-feel stay in the section's own CSS; only these two data
  attributes are load-bearing for the JS).

  Usage:
  <mobile-carousel data-autoplay-seconds="3">
    <div data-carousel-track>...one full-width slide per child...</div>
    <div data-carousel-dots>...one <button> per slide...</div>
  </mobile-carousel>

  Autoplay loops forward continuously; pauses on hover/focus, and stops
  for good after the first manual touch/drag so it doesn't fight a
  visitor who's actively browsing.
*/
class MobileCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.dotsWrap = this.querySelector('[data-carousel-dots]');
    this.dots = this.dotsWrap ? Array.from(this.dotsWrap.children) : [];
    this.currentIndex = 0;

    if (!this.track || this.slides.length < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.pause();
        this.goTo(index);
      });
    });

    this.track.addEventListener('scroll', this.onScroll.bind(this));
    this.track.addEventListener('pointerdown', () => this.pause(), { once: true });
    this.updateActiveDot();

    const seconds = parseFloat(this.dataset.autoplaySeconds);
    if (seconds > 0) {
      this.autoplayDelay = seconds * 1000;
      this.play();
      this.addEventListener('mouseenter', this.pause.bind(this));
      this.addEventListener('mouseleave', this.play.bind(this));
      this.addEventListener('focusin', this.pause.bind(this));
      this.addEventListener('focusout', this.play.bind(this));
    }
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  onScroll() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => this.updateActiveDot(), 80);
  }

  updateActiveDot() {
    let closest = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });
    this.currentIndex = closest;
    this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === closest));
  }

  play() {
    this.pause();
    this.timer = setInterval(() => this.next(), this.autoplayDelay);
  }

  pause() {
    clearInterval(this.timer);
  }

  next() {
    this.goTo((this.currentIndex + 1) % this.slides.length);
  }
}

customElements.define('mobile-carousel', MobileCarousel);
